import React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

/**
 * Dense server-side pagination bar. Shows page X of Y, current row range,
 * page-size selector, and prev/next controls. Consumers own `page`/`pageSize`
 * state and reload data on change.
 *
 * Props:
 *   page          — current page (1-based)
 *   pageSize      — rows per page
 *   total         — total row count from server envelope
 *   onPageChange  — (newPage: number) => void
 *   onPageSizeChange — (newSize: number) => void
 *   pageSizeOptions — array of numbers, default [25, 50, 100, 250]
 *   loading       — optional bool; disables controls
 *   testIdPrefix  — string used to namespace data-testids, e.g. "orders"
 */
export default function PaginationBar({
  page,
  pageSize,
  total = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100, 250],
  loading = false,
  testIdPrefix = "pagination",
}) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const clampedPage = Math.min(Math.max(1, page), totalPages);
  const from = total === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
  const to = Math.min(clampedPage * pageSize, total);
  const canPrev = !loading && clampedPage > 1;
  const canNext = !loading && clampedPage < totalPages;

  const go = (p) => {
    if (p < 1 || p > totalPages || p === clampedPage) return;
    onPageChange(p);
  };

  const btn = "h-7 w-7 inline-flex items-center justify-center rounded-sm border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div className="flex items-center gap-3 text-[11.5px] text-slate-600 font-mono select-none" data-testid={`${testIdPrefix}-pagination`}>
      <span data-testid={`${testIdPrefix}-page-range`}>
        {total === 0 ? "0 rows" : <>{from}–{to} of <b className="text-slate-900">{total}</b></>}
      </span>
      <div className="flex items-center gap-1">
        <button onClick={() => go(1)} disabled={!canPrev} className={btn} data-testid={`${testIdPrefix}-first`} title="First page">
          <ChevronsLeft size={13} />
        </button>
        <button onClick={() => go(clampedPage - 1)} disabled={!canPrev} className={btn} data-testid={`${testIdPrefix}-prev`} title="Previous page">
          <ChevronLeft size={13} />
        </button>
        <span className="px-1.5" data-testid={`${testIdPrefix}-page-indicator`}>
          <b className="text-slate-900">{clampedPage}</b> / {totalPages}
        </span>
        <button onClick={() => go(clampedPage + 1)} disabled={!canNext} className={btn} data-testid={`${testIdPrefix}-next`} title="Next page">
          <ChevronRight size={13} />
        </button>
        <button onClick={() => go(totalPages)} disabled={!canNext} className={btn} data-testid={`${testIdPrefix}-last`} title="Last page">
          <ChevronsRight size={13} />
        </button>
      </div>
      <label className="flex items-center gap-1">
        <span className="text-slate-500 text-[11px]">rows/page</span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          disabled={loading}
          data-testid={`${testIdPrefix}-page-size`}
          className="h-7 px-1 text-[11.5px] border border-slate-200 rounded-sm bg-white font-mono focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
